import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import RightModalLayout from '../../../../layouts/Modal/RightModalLayout';
import ModalSection from '../ModalSection';
import CardButton from '../CardButton';
import PaginationMC from '../../../../components/PaginationMC';
import IconMC from '../../../../components/IconMC';
import { SIDEBAR_ACTIONS } from '../../../../../constants/actionsConstants';
import AssignmentModel from '../../../../models/AssignmentModel';
import TeamAssignmentModel from '../../../../../components/TeamAssignment/TeamAssignmentModel';
import StaffModel from '../../../../../components/Staff/StaffModel';

// Feature flag - temporarily disable agent assignments
const AGENT_ASSIGNMENTS_ENABLED = false;
const SAVED_ITEMS_PAGE_SIZE = 5;
const STAFF_PATH = `/retailers/${ENV.SLUG}/team_assignments`;
const staffLinkClassName = 'tw-text-blue-600 hover:tw-text-blue-700';

const useAssignments = () => {
  const { t } = useTranslation('ChatbotIA');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const getTotalFromResponse = useCallback((response) => {
    return (
      response?.total ??
      response?.meta?.total ??
      response?.pagination?.total ??
      response?.pagination?.total_count ??
      response?.total_count ??
      null
    );
  }, []);

  const loadAssignments = useCallback(async (nextPage = 1) => {
    setLoading(true);
    try {
      const response = await AssignmentModel.get({ page: nextPage, per_page: SAVED_ITEMS_PAGE_SIZE, sort: '-created_at' });
      const rawItems = (
        (Array.isArray(response?.data) && response.data) ||
        response?.data?.data ||
        response?.assignments ||
        []
      );
      const fullList = Array.isArray(rawItems) ? rawItems : [];
      const total = getTotalFromResponse(response) ?? fullList.length;
      const newItems = fullList.length > SAVED_ITEMS_PAGE_SIZE
        ? fullList.slice((nextPage - 1) * SAVED_ITEMS_PAGE_SIZE, nextPage * SAVED_ITEMS_PAGE_SIZE)
        : fullList;

      setItems(newItems);
      setPage(nextPage);
      setTotal(total);
    } catch (error) {
      showtoast(error.error || t('config.error_loading'));
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [t, getTotalFromResponse]);

  const assignments = useMemo(() => ({
    teams: items.filter((a) => a.assignable_type === 'team'),
    agents: items.filter((a) => a.assignable_type === 'agent')
  }), [items]);

  return { assignments, loadAssignments, loading, page, total };
};

const useAvailableOptions = () => {
  const { t } = useTranslation('ChatbotIA');
  const [availableOptions, setAvailableOptions] = useState({
    teams: [],
    agents: []
  });
  const [loading, setLoading] = useState(false);

  const loadOptions = useCallback(async () => {
    setLoading(true);
    try {
      if (AGENT_ASSIGNMENTS_ENABLED) {
        const [teamsResponse, agentsResponse] = await Promise.all([
          TeamAssignmentModel.get(),
          StaffModel.get()
        ]);

        const teams = teamsResponse.team_assignments || [];
        const staff = agentsResponse.staff || [];
        const agents = staff.filter(member => member.active);

        setAvailableOptions({ teams, agents });
      } else {
        const teamsResponse = await TeamAssignmentModel.getForAll();
        const teams = teamsResponse.team_assignments || [];
        setAvailableOptions({ teams, agents: [] });
      }
    } catch (error) {
      showtoast(error.error || t('config.error_loading'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  return { availableOptions, loadOptions, loading };
};

const AssignmentsModal = () => {
  const { t } = useTranslation('ChatbotIA');
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const [cards, setCards] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [showActionSelector, setShowActionSelector] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const { assignments, loadAssignments, page: assignmentsPage, total: assignmentsTotal, loading: loadingAssignments } = useAssignments();
  const { availableOptions, loadOptions } = useAvailableOptions();

  const customSelectStyles = useMemo(() => ({
    control: (provided) => ({
      ...provided,
      backgroundColor: '#F7F8FD',
      border: '1px solid #E5E7EB',
      borderRadius: '12px',
      minHeight: '48px',
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#E5E7EB'
      }
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#6B7280',
      fontSize: '14px',
      fontWeight: '400'
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9CA3AF',
      fontSize: '14px'
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: '#6B7280',
      '&:hover': {
        color: '#6B7280'
      }
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999
    })
  }), []);

  useEffect(() => {
    loadAssignments(1);
    loadOptions();
  }, [loadAssignments, loadOptions]);

  const getTeamOptions = useMemo(() => {
    if (!availableOptions.teams.length) return [];

    return availableOptions.teams.map(team => {
      const isAlreadyAssigned = assignments.teams.some(assignment =>
        assignment.name === team.name
      );

      return {
        value: team.name,
        label: team.name,
        isDisabled: isAlreadyAssigned,
        team: team
      };
    });
  }, [availableOptions.teams, assignments.teams]);

  const getAgentOptions = useMemo(() => {
    if (!AGENT_ASSIGNMENTS_ENABLED || !availableOptions.agents.length) return [];

    return availableOptions.agents.map(agent => {
      const agentName = `${agent.first_name} ${agent.last_name}`.trim() || agent.email;
      const isAlreadyAssigned = assignments.agents.some(assignment =>
        assignment.assignable_id === agent.id.toString() ||
        assignment.name === agentName
      );

      return {
        value: agent.id,
        label: agentName,
        isDisabled: isAlreadyAssigned,
        agent: agent
      };
    });
  }, [availableOptions.agents, assignments.agents]);

  const handleEditAssignment = useCallback((assignment) => {
    const actionType = assignment.assignable_type === 'team' ? 'assign_to_team' : 'assign_to_agent';
    const newCard = {
      id: Date.now(),
      action: actionType,
      teamName: assignment.assignable_type === 'team' ? assignment.name : '',
      teamInstructions: assignment.assignable_type === 'team' ? assignment.condition || '' : '',
      selectedAgents: assignment.assignable_type === 'agent' ? [{
        value: parseInt(assignment.assignable_id) || assignment.name,
        label: assignment.name
      }] : [],
      agentInstructions: assignment.assignable_type === 'agent' ? assignment.condition || '' : '',
      actionPreselected: true,
      isEditing: true,
      editingId: assignment.id,
      editingName: assignment.name
    };

    setCards([newCard]);
    setEditingItem({
      type: assignment.assignable_type === 'team' ? 'team' : 'agent',
      id: assignment.id,
      name: assignment.name
    });
    setShowActionSelector(false);
  }, []);

  const handleDeleteAssignment = useCallback((assignment) => {
    AssignmentModel.delete(assignment.id)
      .then(() => {
        showtoast(t('config.assignments.delete_success'));
        loadAssignments(1);
      })
      .catch((err) => {
        showtoast(err.message || t('config.assignments.delete_error'));
      });
  }, [t, loadAssignments]);

  const validateDuplicates = useCallback((card) => {
    if (card.isEditing && card.editingId) return true;

    if (card.action === 'assign_to_team') {
      return !assignments.teams.some(assignment =>
        assignment.name.toLowerCase() === card.teamName.toLowerCase()
      );
    } else {
      return !card.selectedAgents.some(agent =>
        assignments.agents.some(assignment =>
          assignment.assignable_id === agent.value ||
          assignment.name.toLowerCase() === agent.label.toLowerCase()
        )
      );
    }
  }, [assignments.teams, assignments.agents]);

  const updateCard = useCallback((cardId, updates) => {
    setCards((prevCards) =>
      prevCards.map((c) =>
        c.id === cardId ? { ...c, ...updates } : c
      )
    );

    if (showValidationErrors) {
      setShowValidationErrors(false);
    }
  }, [showValidationErrors]);

  const getSelectedAgentOption = useCallback((selectedAgents) => {
    if (!AGENT_ASSIGNMENTS_ENABLED) return null;

    const selectedAgent = selectedAgents?.[0];
    if (!selectedAgent) return null;

    return getAgentOptions.find(option =>
      option.value === selectedAgent.value ||
      option.value.toString() === selectedAgent.value?.toString() ||
      option.label === selectedAgent.label
    ) || null;
  }, [getAgentOptions]);



  const handleSave = useCallback(() => {
    const hasValidData = cards.some(card =>
      (card.action === 'assign_to_team' && card.teamName && card.teamInstructions?.trim()) ||
      (card.action === 'assign_to_agent' && card.selectedAgents && card.selectedAgents.length > 0 && card.agentInstructions?.trim())
    );

    if (!hasValidData) {
      setShowValidationErrors(true);
      return;
    }

    for (const card of cards) {
      if (!validateDuplicates(card)) {
        const errorKey = card.action === 'assign_to_team'
          ? 'config.assignments.duplicate_team_error'
          : 'config.assignments.duplicate_agent_error';
        showtoast(t(errorKey));
        return;
      }
    }

    setSaving(true);

    const card = cards[0];
    let assignmentParams;

    if (card.action === 'assign_to_team') {
      const selectedTeam = availableOptions.teams.find(team => team.name === card.teamName);
      assignmentParams = {
        assignable_type: 'team',
        assignable_id: selectedTeam?.id || card.teamName,
        name: card.teamName,
        condition: card.teamInstructions || ''
      };
    } else {
      const selectedAgent = card.selectedAgents?.[0];
      assignmentParams = {
        assignable_type: 'agent',
        assignable_id: selectedAgent?.value || '',
        name: selectedAgent?.label || '',
        condition: card.agentInstructions || ''
      };
    }

    (card.isEditing && card.editingId
      ? AssignmentModel.update(card.editingId, assignmentParams)
      : AssignmentModel.save(assignmentParams)
    )
      .then(() => {
        showtoast(editingItem ? t('config.assignments.edit_success') : t('config.save_success'));
        setSaving(false);
        setEditingItem(null);
        setCards([]);
        setShowActionSelector(false);
        setShowValidationErrors(false);
        loadAssignments(1);
      })
      .catch((error) => {
        setSaving(false);
        showtoast(error.message || (editingItem ? t('config.assignments.edit_error') : t('config.save_error')));
      });
  }, [cards, assignments.teams, assignments.agents, availableOptions.teams, editingItem, t, loadAssignments]);

  const addCard = useCallback(() => {
    setShowActionSelector(true);
  }, []);

  const handleActionSelect = useCallback((actionType) => {
    const newCard = {
      id: Date.now(),
      action: actionType,
      actionPreselected: true
    };

    if (actionType === 'assign_to_team') {
      newCard.teamName = '';
      newCard.teamInstructions = '';
    } else if (actionType === 'assign_to_agent') {
      newCard.selectedAgents = [];
      newCard.agentInstructions = '';
    }

    setCards([newCard]);
    setShowActionSelector(false);
  }, []);

  const getModalTitle = useMemo(() => {
    if (showActionSelector) return t('config.assignments.choose_action_title');

    const preselectedCard = cards.find(card => card.actionPreselected);
    if (!preselectedCard) return t('config.assignments.modal_title');

    const isEditing = preselectedCard.isEditing;
    const isTeam = preselectedCard.action === 'assign_to_team';

    const titleKey = `config.assignments.${isEditing ? 'edit' : 'assign_to'}_${isTeam ? 'team' : 'agent'}_title`;
    return t(titleKey);
  }, [showActionSelector, cards, t]);

  const visibleTeamAssignments = assignments.teams;
  const visibleAgentAssignments = assignments.agents;
  const totalAssignmentsPages = Math.ceil((assignmentsTotal || 0) / SAVED_ITEMS_PAGE_SIZE);

  const handleAssignmentsPageChange = ({ selected }) => {
    loadAssignments(selected + 1);
  };

  return (
    <div className="tw-wrapper">
      <RightModalLayout
        title={getModalTitle}
        submitAction={cards.some(card => card.actionPreselected) ? handleSave : null}
        submitLabel={cards.some(card => card.actionPreselected) ?
          (cards.some(card => card.isEditing) ? t('config.assignments.update') : t('config.assignments.create'))
          : null}
        submitDisabled={saving}
        showBackButton={showActionSelector || cards.some(card => card.actionPreselected)}
        closeAction={showActionSelector ? () => setShowActionSelector(false) : cards.some(card => card.actionPreselected) ? () => {
          const isEditingMode = cards.some(card => card.isEditing);
          setCards([]);
          setEditingItem(null);
          setShowValidationErrors(false);
          if (isEditingMode) {
            setShowActionSelector(false);
          } else {
            setShowActionSelector(true);
          }
        } : null}
        cancelAction={showActionSelector ? () => setShowActionSelector(false) : cards.some(card => card.actionPreselected) ? () => {
          setCards([]);
          setShowValidationErrors(false);
          setShowActionSelector(false);
          dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
        } : (!showActionSelector && !cards.some(card => card.actionPreselected)) ? () => {
          dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
        } : null}
        cancelLabel={showActionSelector ? t('config.assignments.back_button') : cards.some(card => card.actionPreselected) ? t('config.assignments.cancel_button') : (!showActionSelector && !cards.some(card => card.actionPreselected)) ? t('config.assignments.close') : null}
        footerVariant={cards.some(card => card.actionPreselected) ? 'VERTICAL' : 'HORIZONTAL'}
        footerClassName={cards.some(card => card.actionPreselected) ? 'custom-vertical-footer' : ''}
      >
        {cards.some(card => card.actionPreselected) ? (
          <>
            {cards.filter(card => card.actionPreselected).map((card) => (
              <div key={card.id}>
                {card.action === 'assign_to_team' && (
                  <div>
                    <div className="figma-field">
                      <label htmlFor={`team-select-${card.id}`} className="fake-label">
                        {t('config.assignments.team_name_label')}
                        {showValidationErrors && !card.teamName && (
                          <span className="tw-ml-2 tw-text-red-500 tw-text-xs">
                            {t('config.empty_error')}
                          </span>
                        )}
                      </label>
                      <Select
                        options={getTeamOptions}
                        value={getTeamOptions.find(option =>
                          option.value === card.teamName || option.label === card.teamName
                        ) || null}
                        id={`team-select-${card.id}`}
                        className="select-tag"
                        classNamePrefix="select-tag"
                        styles={customSelectStyles}
                        placeholder={t('config.assignments.team_name_placeholder')}
                        isOptionDisabled={(option) => option.isDisabled}
                        isSearchable={!card.isEditing}
                        openMenuOnClick={!card.isEditing}
                        noOptionsMessage={() => t('config.assignments.no_teams_available')}
                        onChange={(selectedOption) => {
                          updateCard(card.id, {
                            teamName: selectedOption ? selectedOption.value : ''
                          });
                        }}
                        formatOptionLabel={(option) => (
                          <div
                            style={{
                              opacity: (option.isDisabled && !card.isEditing) ? 0.5 : 1,
                              color: card.isEditing ? '#000000' : (option.isDisabled ? '#999' : 'inherit')
                            }}
                            title={option.isDisabled ? t('config.assignments.duplicate_team_error') : ""}
                          >
                            {option.label}
                          </div>
                        )}
                        components={{
                          IndicatorSeparator: () => null,
                          DropdownIndicator: card.isEditing ? () => null : undefined
                        }}
                      />
                    </div>
                    <div className="mb-0 mt-5">
                      <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                        {t('config.assignments.team_instructions_label')}
                      </label>
                      <p className="tw-text-xs tw-text-gray-600 tw-mb-2">
                        {t('config.assignments.instructions_1_description')}
                      </p>
                    </div>

                    <div className="figma-field">
                      <label htmlFor={`team-condition-${card.id}`} className="fake-label">
                        {t('config.assignments.team_condition_label')}
                        {showValidationErrors && !card.teamInstructions?.trim() && (
                          <span className="tw-ml-2 tw-text-red-500 tw-text-xs">
                            {t('config.empty_error')}
                          </span>
                        )}
                      </label>
                      <div className="tw-relative">
                        <textarea
                          id={`team-condition-${card.id}`}
                          className="form-control"
                          rows="8"
                          maxLength="250"
                          value={card.teamInstructions || ''}
                          placeholder={t('config.assignments.team_condition_placeholder')}
                          onChange={(e) => {
                            updateCard(card.id, { teamInstructions: e.target.value });
                          }}
                        />
                        <div className="tw-absolute tw-bottom-2 tw-right-3 tw-text-xs tw-text-gray-400">
                          {(card.teamInstructions || '').length}/250
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {AGENT_ASSIGNMENTS_ENABLED && card.action === 'assign_to_agent' && (
                  <div>
                    <div className="figma-field">
                      <label htmlFor={`agent-select-${card.id}`} className="fake-label">
                        {t('config.assignments.agent_name_label')}
                        {showValidationErrors && (!card.selectedAgents || card.selectedAgents.length === 0) && (
                          <span className="tw-ml-2 tw-text-red-500 tw-text-xs">
                            {t('config.empty_error')}
                          </span>
                        )}
                      </label>
                      <Select
                        options={getAgentOptions}
                        value={getSelectedAgentOption(card.selectedAgents)}
                        id={`agent-select-${card.id}`}
                        className="select-tag"
                        classNamePrefix="select-tag"
                        styles={customSelectStyles}
                        placeholder={t('config.assignments.agent_name_placeholder')}
                        isOptionDisabled={(option) => option.isDisabled}
                        noOptionsMessage={() => t('config.assignments.no_agents_available')}
                        onChange={(selectedOption) => {
                          const newAgent = selectedOption ? {
                            value: selectedOption.value,
                            label: selectedOption.label
                          } : null;
                          updateCard(card.id, {
                            selectedAgents: newAgent ? [newAgent] : []
                          });
                        }}
                        formatOptionLabel={(option) => (
                          <div
                            style={{
                              opacity: option.isDisabled ? 0.5 : 1,
                              color: option.isDisabled ? '#999' : 'inherit'
                            }}
                            title={option.isDisabled ? t('config.assignments.duplicate_agent_error') : ""}
                          >
                            {option.label}
                          </div>
                        )}
                        components={{
                          IndicatorSeparator: () => null
                        }}
                      />

                    </div>

                    <div className="mb-0 mt-5">
                      <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                        {t('config.assignments.agent_instructions_label')}
                      </label>
                      <p className="tw-text-xs tw-text-gray-600 tw-mb-2">
                        {t('config.assignments.instructions_1_description')}
                      </p>
                    </div>

                    <div className="figma-field">
                      <label htmlFor={`agent-condition-${card.id}`} className="fake-label">
                        {t('config.assignments.agent_condition_label')}
                        {showValidationErrors && !card.agentInstructions?.trim() && (
                          <span className="tw-ml-2 tw-text-red-500 tw-text-xs">
                            {t('config.empty_error')}
                          </span>
                        )}
                      </label>
                      <div className="tw-relative">
                        <textarea
                          id={`agent-condition-${card.id}`}
                          className="form-control"
                          rows="8"
                          maxLength="250"
                          value={card.agentInstructions || ''}
                          placeholder={t('config.assignments.agent_condition_placeholder')}
                          onChange={(e) => {
                            updateCard(card.id, { agentInstructions: e.target.value });
                          }}
                        />
                        <div className="tw-absolute tw-bottom-2 tw-right-3 tw-text-xs tw-text-gray-400">
                          {(card.agentInstructions || '').length}/250
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </>
        ) : showActionSelector ? (
          <div className="tw-space-y-4">
            <div onClick={() => handleActionSelect('assign_to_team')}>
              <CardButton
                icon="people-outline"
                isIcon={true}
                title={t('config.assignments.save_assignment_team')}
                description={t('config.assignments.save_assignment_team_description')}
              />
            </div>

            {AGENT_ASSIGNMENTS_ENABLED && (
              <div onClick={() => handleActionSelect('assign_to_agent')}>
                <CardButton
                  icon="person-outline"
                  isIcon={true}
                  title={t('config.assignments.save_assignment_agent')}
                  description={t('config.assignments.save_assignment_agent_description')}
                />
              </div>
            )}
          </div>
        ) : (
          <>
            <ModalSection
              title={t('config.assignments.section_1_title')}
              description={t('config.assignments.section_1_description')}
              afterContent={
                <>
                  <div className="tw-flex tw-gap-2 tw-px-4 tw-py-3 tw-mt-3 tw-rounded-xl tw-border tw-border-yellow-100 tw-bg-yellow-50">
                    <IconMC name="info-outline" size="base" className="tw-text-yellow-600 tw-shrink-0 tw-mt-0.5" />
                    <span className="tw-text-xs tw-text-yellow-700 tw-font-medium">
                      <Trans
                        i18nKey="config.assignments.teams_hint"
                        ns="ChatbotIA"
                        components={{
                          bold: <strong className="tw-font-semibold" />,
                          staffLink: (
                            <a
                              href={STAFF_PATH}
                              className={staffLinkClassName}
                              target="_blank"
                              rel="noopener noreferrer"
                            />
                          )
                        }}
                      />
                    </span>
                  </div>
                  <button
                    type="button"
                    className="tw-w-full tw-px-4 tw-mt-5 tw-py-2 tw-border tw-border-blue-500 tw-text-blue-500 tw-bg-white tw-rounded-lg tw-font-medium tw-text-sm hover:tw-bg-blue-50 tw-transition-colors"
                    onClick={addCard}
                  >
                    {t('config.assignments.button_1')}
                  </button>
                </>
              }
            />

            <div className="tw-pt-5">
              <div className="tw-mt-6">
                <h4 className="tw-font-semibold tw-text-gray-950 tw-text-sm tw-mb-3">
                  {t('config.assignments.saved_assignments_teams_title')}
                </h4>
                    {assignments.teams.length === 0 ? (
                      <p className="tw-bg-gray-50 center-flex tw-p-5 tw-rounded-lg tw-text-blue-500">
                        {t('config.assignments.no_saved_assignments')}
                      </p>
                    ) : (
                      <div className="tw-space-y-2">
                        {visibleTeamAssignments.map((assignment) => (
                          <div
                            key={assignment.id}
                            className="tw-flex tw-justify-between tw-items-center tw-p-3 tw-rounded-lg tw-bg-gray-50"
                          >
                            <div className="tw-flex-1">
                              <div className="tw-font-medium tw-text-sm tw-text-gray-900">
                                {assignment.name}
                              </div>
                              {assignment.condition && (
                                <div className="tw-text-xs tw-text-gray-500 tw-mt-1">
                                  {assignment.condition}
                                </div>
                              )}
                            </div>
                            <div className="tw-flex tw-gap-2">
                              <button
                                type="button"
                                className="tw-text-blue-500 hover:tw-text-blue-700 tw-transition-colors"
                                onClick={() => handleEditAssignment(assignment)}
                                title={t('config.assignments.edit_assignment')}
                              >
                                <i className="mi-edit-outline fz-16"></i>
                              </button>
                              <button
                                type="button"
                                className="tw-text-red-500 hover:tw-text-red-700 tw-transition-colors"
                                onClick={() => handleDeleteAssignment(assignment)}
                                title={t('config.assignments.delete_assignment')}
                              >
                                <i className="mi-trash-2-outline fz-16"></i>
                              </button>
                            </div>
                          </div>
                        ))}
                        {totalAssignmentsPages > 1 && (
                          <div className="tw-mt-3">
                            <PaginationMC
                              onPageChange={handleAssignmentsPageChange}
                              totalPages={totalAssignmentsPages}
                              currentPage={assignmentsPage}
                              variant="minimalJustifyCenter"
                              pageRangeDisplayed={2}
                              marginPagesDisplayed={0}
                              disabled={loadingAssignments}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {AGENT_ASSIGNMENTS_ENABLED && (
                    <div className="tw-mt-6">
                      <h4 className="tw-font-semibold tw-text-gray-950 tw-text-sm tw-mb-3">
                        {t('config.assignments.saved_assignments_agents_title')}
                      </h4>
                      {assignments.agents.length === 0 ? (
                        <p className="tw-bg-gray-50 center-flex tw-p-5 tw-rounded-lg tw-text-blue-500">
                          {t('config.assignments.no_saved_assignments')}
                        </p>
                      ) : (
                        <div className="tw-space-y-2">
                          {visibleAgentAssignments.map((assignment) => (
                            <div
                              key={assignment.id}
                              className="tw-flex tw-justify-between tw-items-center tw-p-3 tw-rounded-lg tw-bg-gray-50"
                            >
                              <div className="tw-flex-1">
                                <div className="tw-font-medium tw-text-sm tw-text-gray-900">
                                  {assignment.name}
                                </div>
                                {assignment.condition && (
                                  <div className="tw-text-xs tw-text-gray-500 tw-mt-1">
                                    {assignment.condition}
                                  </div>
                                )}
                              </div>
                              <div className="tw-flex tw-gap-2">
                                <button
                                  type="button"
                                  className="tw-text-blue-500 hover:tw-text-blue-700 tw-transition-colors"
                                  onClick={() => handleEditAssignment(assignment)}
                                  title={t('config.assignments.edit_assignment')}
                                >
                                  <i className="mi-edit-outline fz-16"></i>
                                </button>
                                <button
                                  type="button"
                                  className="tw-text-red-500 hover:tw-text-red-700 tw-transition-colors"
                                  onClick={() => handleDeleteAssignment(assignment)}
                                  title={t('config.assignments.delete_assignment')}
                                >
                                  <i className="mi-trash-2-outline fz-16"></i>
                                </button>
                              </div>
                            </div>
                          ))}
                          {totalAssignmentsPages > 1 && (
                            <div className="tw-mt-3">
                              <PaginationMC
                                onPageChange={handleAssignmentsPageChange}
                                totalPages={totalAssignmentsPages}
                                currentPage={assignmentsPage}
                                variant="minimalJustifyCenter"
                                pageRangeDisplayed={2}
                                marginPagesDisplayed={0}
                                disabled={loadingAssignments}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
            </div>
          </>
        )}
      </RightModalLayout>
    </div>
  );
};

export default AssignmentsModal;

const style = document.createElement('style');
style.textContent = `
  .custom-vertical-footer .tw-order-2 {
    background-color: #f9fafb !important;
    color: #4b5563 !important;
    border: 1px solid #e5e7eb !important;
  }
  .custom-vertical-footer .tw-order-2:hover {
    background-color: #d1d5db !important;
    color: #374151 !important;
    border-color: #d1d5db !important;
  }
  .custom-vertical-footer .tw-order-2:active {
    background-color: #9ca3af !important;
    border-color: #9ca3af !important;
  }
`;
if (!document.head.querySelector('style[data-custom-footer-assignments]')) {
  style.setAttribute('data-custom-footer-assignments', 'true');
  document.head.appendChild(style);
}
